import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { updateOnboardingSession } from '../../../../services/wizardApi';
import { useStartUIDiscovery } from '../../../../hooks/API/wizard.api';
import { toast } from 'react-toast';
import { LuGlobe, LuLock, LuRadar, LuInfo, LuEye, LuEyeOff } from 'react-icons/lu';
import { getUIDiscoveryStepNumbers } from '../../../../Config/Onboarding/stepFlow';
import { getApiErrorMessage } from '../../../../utils/apiError';
import ExecutionModeModal, { EXECUTION_MODES } from './ExecutionModeModal';
import '../../../../styles/wizard.css';

const LOCAL_AGENT_START_TIMEOUT_MS = 30000;

const Step6CDiscoverySetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { project, uiDiscovery, setUiDiscovery, nextStep, previousStep, sessionId, testCreationMode } = useOnboarding();
  const discoverySteps = getUIDiscoveryStepNumbers(searchParams.get('mode') || testCreationMode);
  const { mutateAsync: startDiscovery } = useStartUIDiscovery();
  const [formData, setFormData] = useState({
    baseUrl: uiDiscovery.baseUrl || project.baseUrl || '',
    maxDepth: 3,
    maxPages: 100,
    requireAuth: false,
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isStarting, setIsStarting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showExecutionModeModal, setShowExecutionModeModal] = useState(false);
  const isMountedRef = useRef(true);
  const agentListenerCleanupRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.baseUrl.trim()) {
      newErrors.baseUrl = 'Base URL is required';
    } else {
      try {
        new URL(formData.baseUrl);
      } catch (e) {
        newErrors.baseUrl = 'Please enter a valid URL';
      }
    }

    if (formData.requireAuth) {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required for authenticated discovery';
      }
      if (!formData.password.trim()) {
        newErrors.password = 'Password is required for authenticated discovery';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getProjectId = () => project.project_id || project.projectId || project.id;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      agentListenerCleanupRef.current?.();
    };
  }, []);

  // Resolves true on DISCOVERY_START_SUCCESS for this run, false on timeout
  const waitForLocalAgentStart = (runId) =>
    new Promise((resolve) => {
      agentListenerCleanupRef.current?.();

      const handleMessage = (event) => {
        if (event.source !== window) return;
        const data = event.data;
        if (
          data?.source === 'ROBITEST_AGENT' &&
          data.type === 'DISCOVERY_START_SUCCESS' &&
          data.runId === runId
        ) {
          cleanup();
          resolve(true);
        }
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        resolve(false);
      }, LOCAL_AGENT_START_TIMEOUT_MS);

      const cleanup = () => {
        clearTimeout(timeoutId);
        window.removeEventListener('message', handleMessage);
        agentListenerCleanupRef.current = null;
      };

      agentListenerCleanupRef.current = cleanup;
      window.addEventListener('message', handleMessage);
    });

  const handleOpenExecutionModeModal = () => {
    if (!validate()) return;

    if (!getProjectId()) {
      toast.error('Project is required to start discovery');
      return;
    }

    setShowExecutionModeModal(true);
  };

  const handleStartDiscovery = async (executionMode) => {
    const projectId = getProjectId();
    setIsStarting(true);

    try {
      const payload = {
        base_url: formData.baseUrl.trim(),
        max_depth: Number(formData.maxDepth),
        max_pages: Number(formData.maxPages),
        requires_authentication: Boolean(formData.requireAuth),
        execution_mode: executionMode
      };

      if (payload.requires_authentication) {
        payload.username = formData.username.trim();
        payload.password = formData.password;
      }

      const response = await startDiscovery({ projectId, payload });

      if (response?.status_code === 200 && response.run_id) {
        if (executionMode === EXECUTION_MODES.LOCAL) {
          // Listener must be attached before posting so a fast agent reply isn't missed
          const agentStarted = waitForLocalAgentStart(response.run_id);
          window.postMessage({
            source: "ROBITEST_CLOUD",
            action: 'TRIGGER_UI_DISCOVERY',
            runId: response.run_id,
            projectId,
            baseUrl: payload.base_url,
            maxDepth: payload.max_depth,
            maxPages: payload.max_pages,
            authenticationRequired: payload.requires_authentication,
            ...(payload.requires_authentication && {
              username: payload.username,
              password: payload.password
            }),
          }, '*');

          const started = await agentStarted;
          if (!isMountedRef.current) return;
          if (!started) {
            toast.error('Local agent did not respond. Make sure it is installed and running, then try again.');
            return;
          }
        }

        setShowExecutionModeModal(false);
        setUiDiscovery({
          discoveryJobId: response.run_id,
          runId: response.run_id,
          status: 'started',
          baseUrl: formData.baseUrl,
          credentials: formData.requireAuth ? { username: formData.username } : null
        });

        if (sessionId) {
          await updateOnboardingSession(sessionId, {
            currentStep: '6C',
            uiDiscovery: {
              discoveryJobId: response.run_id,
              runId: response.run_id,
              baseUrl: formData.baseUrl
            }
          });
        }

        nextStep();
        navigate(`/app/onboarding?step=${discoverySteps.crawl}&mode=${discoverySteps.modeQuery}`);
      } else {
        toast.error(response?.message || 'Failed to start UI discovery');
      }
    } catch (error) {
      console.error('Error starting discovery:', error);
      toast.error(getApiErrorMessage(error, 'Failed to start discovery. Please try again.'));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper
          currentStep={discoverySteps.setup}
          completedSteps={
            discoverySteps.aiPreview
              ? [1, 2, 3, 4, 5, discoverySteps.aiPreview, discoverySteps.setupTools]
              : [1, 2, 3, 4, 5]
          }
        />
        
        <WizardCard
          title="UI Discovery Setup"
          subtitle="Automatically crawl and map your application structure"
        >
          <div style={{ marginBottom: '2rem' }}>
            {/* Info Banner */}
            <div style={{ 
              padding: '1rem', 
              backgroundColor: 'var(--info-color-light)',
              borderRadius: '8px',
              border: '1px solid var(--info-color)',
              display: 'flex',
              alignItems: 'start',
              gap: '0.75rem',
              marginBottom: '2rem'
            }}>
              <LuInfo size={20} style={{ color: 'var(--info-color)', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '0.875rem' }}>
                <strong>What is UI Discovery?</strong>
                <p style={{ margin: '0.5rem 0 0' }}>
                  Our crawler will automatically explore your application, discovering pages, forms, buttons, and other UI elements. 
                  This builds a comprehensive UI Map that enables reliable test automation without manual recording.
                </p>
              </div>
            </div>

            {/* Configuration Form */}
            <div className="wizard-form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="wizard-form-label">
                <LuGlobe size={18} style={{ marginRight: '0.5rem' }} />
                Base URL <span className="required">*</span>
              </label>
              <input
                type="url"
                name="baseUrl"
                className="wizard-form-input"
                value={formData.baseUrl}
                onChange={handleChange}
                placeholder="https://your-application.com"
                disabled={isStarting}
              />
              {errors.baseUrl && <p className="wizard-form-error">{errors.baseUrl}</p>}
              <p className="wizard-form-help">
                Starting point for the crawler
              </p>
            </div>

            <div className="wizard-form-row">
              <div className="wizard-form-group">
                <label className="wizard-form-label">Maximum Depth</label>
                <select
                  name="maxDepth"
                  className="wizard-form-select"
                  value={formData.maxDepth}
                  onChange={handleChange}
                  disabled={isStarting}
                >
                  <option value={1}>1 level (shallow)</option>
                  <option value={2}>2 levels</option>
                  <option value={3}>3 levels (recommended)</option>
                  <option value={4}>4 levels</option>
                  <option value={5}>5 levels (deep)</option>
                </select>
                <p className="wizard-form-help">
                  How many clicks deep to explore
                </p>
              </div>

              <div className="wizard-form-group">
                <label className="wizard-form-label">Maximum Pages</label>
                <select
                  name="maxPages"
                  className="wizard-form-select"
                  value={formData.maxPages}
                  onChange={handleChange}
                  disabled={isStarting}
                >
                  <option value={50}>50 pages</option>
                  <option value={100}>100 pages (recommended)</option>
                  <option value={250}>250 pages</option>
                  <option value={500}>500 pages</option>
                </select>
                <p className="wizard-form-help">
                  Limit to prevent excessive crawling
                </p>
              </div>
            </div>

            {/* Authentication Section */}
            <div style={{ 
              marginTop: '2rem',
              padding: '1.5rem',
              backgroundColor: 'var(--card-bg)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)'
            }}>
              <div className="wizard-form-checkbox" style={{ marginBottom: '1rem' }}>
                <input
                  type="checkbox"
                  id="requireAuth"
                  name="requireAuth"
                  checked={formData.requireAuth}
                  onChange={handleChange}
                  disabled={isStarting}
                />
                <label htmlFor="requireAuth" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <LuLock size={16} />
                  Application requires authentication
                </label>
              </div>

              {formData.requireAuth && (
                <div className="wizard-form-row">
                  <div className="wizard-form-group">
                    <label className="wizard-form-label">
                      Username <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="username"
                      className="wizard-form-input"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="test@example.com"
                      disabled={isStarting}
                    />
                    {errors.username && <p className="wizard-form-error">{errors.username}</p>}
                  </div>

                  <div className="wizard-form-group">
                    <label className="wizard-form-label">
                      Password <span className="required">*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        className="wizard-form-input"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        disabled={isStarting}
                        style={{ paddingRight: '2.75rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        disabled={isStarting}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        title={showPassword ? 'Hide password' : 'Show password'}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '0.75rem',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: isStarting ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-secondary)'
                        }}
                      >
                        {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                      </button>
                    </div>
                    {errors.password && <p className="wizard-form-error">{errors.password}</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Estimated Time */}
            <div style={{ 
              marginTop: '1.5rem',
              padding: '1rem',
              backgroundColor: 'var(--card-bg)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              textAlign: 'center'
            }}>
              <LuRadar size={24} style={{ color: 'var(--primary-color)', marginBottom: '0.5rem' }} />
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                <strong>Estimated Time:</strong> 3-10 minutes
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                Larger applications may take longer
              </div>
            </div>
          </div>

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              // UI Discovery: Setup Tools sits directly before this screen.
              const backStep = discoverySteps.setupTools || discoverySteps.aiPreview;
              if (backStep) {
                navigate(`/app/onboarding?step=${backStep}&mode=${discoverySteps.modeQuery}`);
              } else {
                navigate('/app/onboarding?step=5');
              }
            }}
            showNext={true}
            onNext={handleOpenExecutionModeModal}
            nextLabel={
              <>
                <LuRadar size={18} />
                Start Discovery
              </>
            }
            nextLoading={isStarting}
            nextDisabled={!formData.baseUrl.trim()}
          />
        </WizardCard>

        <ExecutionModeModal
          isOpen={showExecutionModeModal}
          onClose={() => setShowExecutionModeModal(false)}
          onConfirm={handleStartDiscovery}
          isLoading={isStarting}
        />
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step6CDiscoverySetup;